// Eigene Passkeys für den Notfall-Login und Writer-Tokens
// für Deployment-Pipelines.
import { startRegistration } from '@simplewebauthn/browser';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import { useEffect, useState } from 'react';

import { fmt, PRODUCTS, slug } from './api.ts';
import type { Api, Me, ProductId } from './api.ts';

type Notify = (msg: string, error?: boolean) => void;
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function Passkeys({ api, me, notify }: { api: Api; me: Me; notify: Notify }) {
  const [list, setList] = useState<Awaited<ReturnType<Api['passkeys']>>>([]);
  const [label, setLabel] = useState('');
  const oidc = me.auth === 'oidc';
  const load = () => {
    if (oidc) void api.passkeys().then(setList, (e: unknown) => notify(errText(e), true));
  };
  useEffect(load, [oidc]);

  async function register() {
    try {
      const options = (await api.passkeyOptions()) as PublicKeyCredentialCreationOptionsJSON;
      const response = await startRegistration({ optionsJSON: options });
      const r = await api.passkeyVerify(label.trim() || 'Passkey', response);
      notify(r.count < 2 ? 'Passkey gespeichert. Bitte noch einen zweiten anlegen (z. B. Hardware-Key).' : 'Passkey gespeichert.');
      setLabel('');
      load();
    } catch (e) {
      notify(errText(e), true);
    }
  }

  if (!oidc) return <p className="empty">Passkeys lassen sich nur in einer normalen Anmeldung verwalten.</p>;
  return (
    <section className="card">
      <h2>Passkeys für die Notfall-Anmeldung</h2>
      <p>
        Fällt authentik aus, meldest du dich hier per Passkey an. Ein Passkey gilt 90 Tage nach deiner letzten normalen Anmeldung, mit
        deinen damaligen Rechten. Empfohlen: zwei Passkeys, z. B. Gerät und Hardware-Key.
      </p>
      <ul>
        {list.map((p) => (
          <li key={p.id}>
            {p.label} – angelegt {fmt(p.created_at)}
            {p.last_used_at && <>, zuletzt benutzt {fmt(p.last_used_at)}</>}{' '}
            <button
              onClick={() =>
                void api.removePasskey(p.id).then(
                  () => load(),
                  (e: unknown) => notify(errText(e), true),
                )
              }
            >
              Entfernen
            </button>
          </li>
        ))}
        {!list.length && <li>Noch kein Passkey.</li>}
      </ul>
      <div className="actions">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Bezeichnung, z. B. MacBook" />
        <button className="primary" onClick={() => void register()}>
          Passkey hinzufügen
        </button>
      </div>
    </section>
  );
}

export function Tokens({ api, notify }: { api: Api; notify: Notify }) {
  const [list, setList] = useState<Awaited<ReturnType<Api['tokens']>>>([]);
  const [name, setName] = useState('');
  const [products, setProducts] = useState<ProductId[]>([]);
  const [issued, setIssued] = useState<string | null>(null);
  const load = () => void api.tokens().then(setList, (e: unknown) => notify(errText(e), true));
  useEffect(load, []);

  async function issue() {
    try {
      const r = await api.issueToken(slug(name), products);
      setIssued(r.token);
      setName('');
      setProducts([]);
      load();
    } catch (e) {
      notify(errText(e), true);
    }
  }

  return (
    <section className="card">
      <h2>Writer-Tokens für Deployment-Pipelines</h2>
      <p>Damit ein Deployment seine Wartung auch bei authentik-Ausfall beenden kann. Nur Wartungen der gewählten Produkte.</p>
      {issued && (
        <div className="banner banner-readonly">
          <strong>Token jetzt kopieren – es wird nicht noch einmal angezeigt:</strong>
          <code className="token">{issued}</code>
          <button onClick={() => setIssued(null)}>Erledigt</button>
        </div>
      )}
      <table>
        <tbody>
          {list.map((t) => (
            <tr key={`${t.name}-${t.created_at}`}>
              <td>{t.name}</td>
              <td>{t.products.join(', ')}</td>
              <td>
                {t.created_by}, {fmt(t.created_at)}
              </td>
              <td>
                {t.revoked_at ? (
                  `widerrufen ${fmt(t.revoked_at)}`
                ) : (
                  <button onClick={() => void api.revokeToken(t.name).then(load, (e: unknown) => notify(errText(e), true))}>
                    Widerrufen
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="chips">
        {PRODUCTS.map((p) => (
          <label key={p.id} className="inline">
            <input
              type="checkbox"
              checked={products.includes(p.id)}
              onChange={() => setProducts((c) => (c.includes(p.id) ? c.filter((x) => x !== p.id) : [...c, p.id]))}
            />{' '}
            {p.name}
          </label>
        ))}
      </div>
      <div className="actions">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name, z. B. planung-deploy" />
        <button className="primary" disabled={!slug(name) || !products.length} onClick={() => void issue()}>
          Token ausstellen
        </button>
      </div>
    </section>
  );
}
